/**
 * Teslyn Cloud's front door (decision 699; docs/specs/cloud/front-door.md §2): `cloud.teslyn.au` is Teslyn's own
 * sign-in, which finds the person's company and sends them on to the company's own address, on the page it was opened
 * with. Nothing travels in the address (security review of 2 Nov). The front door and a company address under the
 * build's cookie domain share one sign-in (decision 978, docs/specs/cloud/shared-sign-in.md), so the person arrives
 * signed in; a company address outside it (its own domain) asks them to sign in once more there. The front door
 * remembers the company in this browser, so the next visit (an alert email's link, decision 741) goes straight on;
 * `?choose` forgets it.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { tenantLogoSize } from '@teslyn/domain';
import lockup from '../../../../brand/logos/teslyn-lockup-horizontal.svg';
import { LinkConfirm } from '../auth/LinkConfirm.tsx';
import { SignIn } from '../auth/SignIn.tsx';
import { isPendingLink, type AuthOutcome } from '../auth/authReturn.ts';
import { useSession } from '../auth/useSession.ts';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PoweredBy } from '../components/States.tsx';
import type { WebConfig } from '../config.ts';
import { HelpPanel } from '../help/HelpPanel.tsx';
import { LegalLinks } from '../legal/LegalPage.tsx';
import { sessionPlace } from '../supabase.ts';
import { bundledTenant } from '../tenant/bundled.ts';
import type { Tenant } from '../tenant/tenant.ts';
import {
  CHOOSE_PARAM,
  companyUrl,
  rememberCompany,
  rememberedCompany,
  type CompanyUrl,
  type Where,
} from './address.ts';
import { loadCompanies, type Company, type FrontDoorAccess } from './api.ts';

/** Teslyn's own look for the sign-in card: no company colours, Teslyn's lockup, "Teslyn Cloud" (brand/README.md). */
export const TESLYN_FRONT_DOOR: Tenant = {
  id: null,
  slug: 'teslyn',
  name: 'Teslyn',
  productTitle: 'Teslyn Cloud',
  clientTitle: 'Teslyn Cloud',
  theme: {},
  logo: lockup,
  favicon: null,
  logoSize: tenantLogoSize(null, null),
  source: 'default',
};

export const CONSOLE_LABEL = 'Braynewave’s console';

/**
 * Said before the person leaves for a company address that does not share this sign-in (its own domain, or a build
 * without the shared sign-in; docs/specs/cloud/front-door.md §2.3).
 */
export const SIGN_IN_AGAIN =
  'You sign in once more on your company’s page, the first time in this browser. After that, this address takes ' +
  'you straight there.';

/** Said when the company's address shares this sign-in (decision 978). */
export const STAY_SIGNED_IN = 'You stay signed in: your company’s page opens without asking again.';

type Here = Where & { readonly origin: string };

export interface FrontDoorProps {
  readonly sb: SupabaseClient;
  readonly config: WebConfig;
  /**
   * Why the sign-in this address brought back did not sign the person in (auth/authReturn.ts), an email link
   * waiting for its button, or null.
   */
  readonly authNotice?: AuthOutcome;
  readonly loc?: Here;
  /** Goes to another address, replacing this one in the history. */
  readonly navigate?: (url: string) => void;
  /** This page keeps its session in the shared cookie (decision 978); from `sessionPlace()` by default. */
  readonly shared?: boolean;
}

const replace = (url: string): void => window.location.replace(url);

/** "Thomson Airborne's Teslyn Cloud" (the bundled name before sign-in), or "your company's Teslyn Cloud". */
function cloudOf(name: string | null): string {
  return name ? `${name}’s Teslyn Cloud` : 'your company’s Teslyn Cloud';
}

export function Opening({ what }: { readonly what: string }): ReactElement {
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="opening-title" aria-busy="true">
        <img className="signin__logo" src={lockup} alt="Teslyn" />
        <h1 id="opening-title" className="title">
          Opening {what}…
        </h1>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
      </footer>
    </div>
  );
}

export function FrontDoor({
  sb,
  config,
  authNotice = null,
  loc = window.location,
  navigate = replace,
  shared,
}: FrontDoorProps): ReactElement {
  const [isShared] = useState(() => shared ?? sessionPlace().kind === 'shared');
  // the remembered company goes straight on, unless the address asks to choose again or brings an email link (its
  // button comes first; the company's address would drop the link)
  const [auto] = useState(() => {
    if (isPendingLink(authNotice)) return null;
    if (new URLSearchParams(loc.search).has(CHOOSE_PARAM)) {
      rememberCompany(null);
      return null;
    }
    const slug = rememberedCompany();
    const target = slug ? companyUrl(slug, config, loc) : null;
    return slug && target ? { slug, url: target.url } : null;
  });
  const [leaving, setLeaving] = useState<string | null>(null);
  useEffect(() => {
    if (auto) navigate(auto.url);
  }, [auto, navigate]);
  if (auto) return <Opening what={cloudOf(bundledTenant(auto.slug)?.name ?? null)} />;
  if (leaving) return <Opening what={leaving} />;
  return (
    <FrontDoorSession
      sb={sb}
      config={config}
      authNotice={authNotice}
      loc={loc}
      navigate={navigate}
      onLeaving={setLeaving}
      shared={isShared}
    />
  );
}

function FrontDoorSession({
  sb,
  config,
  authNotice,
  loc,
  navigate,
  onLeaving,
  shared,
}: {
  readonly sb: SupabaseClient;
  readonly config: WebConfig;
  readonly authNotice: AuthOutcome;
  readonly loc: Here;
  readonly navigate: (url: string) => void;
  readonly onLeaving: (what: string | null) => void;
  readonly shared: boolean;
}): ReactElement {
  const session = useSession(sb);
  const [helpOpen, setHelpOpen] = useState(false);
  // an email link waits for its button (nothing spent until then); its outcome, if words, is the notice
  const [outcome, setOutcome] = useState<AuthOutcome>(authNotice);
  const notice = typeof outcome === 'string' ? outcome : null;
  if (isPendingLink(outcome))
    return <LinkConfirm link={outcome} tenant={TESLYN_FRONT_DOOR} onDone={setOutcome} />;
  if (session.status === 'loading') {
    return (
      <div className="signin">
        <Loading what="your session" />
      </div>
    );
  }
  if (session.status === 'signed-out') {
    return (
      <div className={helpOpen ? 'signin-wrap signin-wrap--help' : 'signin-wrap'}>
        <SignIn
          sb={sb}
          tenant={TESLYN_FRONT_DOOR}
          auth={config.auth}
          redirectTo={loc.origin + loc.pathname + loc.search}
          notice={notice}
          onHelp={() => setHelpOpen(true)}
        />
        {helpOpen && (
          <HelpPanel
            pageId="cloud.front-door"
            roles={null}
            tenantName="Teslyn"
            productTitle="Teslyn Cloud"
            onClose={() => setHelpOpen(false)}
          />
        )}
      </div>
    );
  }
  return (
    <Companies
      key={session.session.user.id}
      sb={sb}
      config={config}
      loc={loc}
      navigate={navigate}
      onLeaving={onLeaving}
      shared={shared}
      userId={session.session.user.id}
      email={session.session.user.email ?? ''}
    />
  );
}

interface Choice {
  readonly key: string;
  readonly slug: string;
  readonly title: string;
  readonly detail: string;
  /** The company's Teslyn Cloud, for "Opening …". */
  readonly what: string;
}

function choicesOf(access: FrontDoorAccess, cfg: WebConfig): Choice[] {
  const out: Choice[] = access.companies.map((c: Company) => ({
    key: `c:${c.slug}`,
    slug: c.slug,
    title: c.name,
    detail: c.as === 'staff' ? 'Staff' : 'Client',
    what: cloudOf(c.name),
  }));
  // Braynewave's console opens on any company's address; the build's default company is the one it uses
  if (access.platformAdmin && cfg.defaultTenant)
    out.push({
      key: 'platform',
      slug: cfg.defaultTenant,
      title: CONSOLE_LABEL,
      detail: 'Platform admin',
      what: CONSOLE_LABEL,
    });
  return out;
}

function Companies({
  sb,
  config,
  loc,
  navigate,
  onLeaving,
  shared,
  userId,
  email,
}: {
  readonly sb: SupabaseClient;
  readonly config: WebConfig;
  readonly loc: Here;
  readonly navigate: (url: string) => void;
  readonly onLeaving: (what: string | null) => void;
  readonly shared: boolean;
  readonly userId: string;
  readonly email: string;
}): ReactElement {
  const [access, setAccess] = useState<FrontDoorAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    loadCompanies(sb, userId)
      .then((a) => live && setAccess(a))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, userId, attempt]);

  const signOut = (): void => {
    void sb.auth.signOut({ scope: 'local' });
  };

  // the company's page has this sign-in already: the same site (a preview), or both under the shared cookie domain
  const signedInThere = (t: CompanyUrl | null): boolean =>
    Boolean(t && (t.sameSite || (shared && t.sharesSignIn)));
  // `?choose` stops at the card even for one company, so the person can sign out and use another account
  const choosing = new URLSearchParams(loc.search).has(CHOOSE_PARAM);

  const go = (c: Choice): void => {
    const target = companyUrl(c.slug, config, loc);
    if (!target) return;
    rememberCompany(c.slug);
    onLeaving(c.what);
    // the sign-in is there already, or it is the shared one, which the other addresses still use (decision 978)
    if (signedInThere(target) || shared) return navigate(target.url);
    // a front door with a sign-in of its own (no shared cookie on this address) keeps none; the company's page has its
    // own
    void sb.auth
      .signOut({ scope: 'local' })
      .catch(() => undefined)
      .then(() => navigate(target.url));
  };

  const choices = access ? choicesOf(access, config) : [];
  const only = access && !access.platformAdmin && choices.length === 1 ? choices[0]! : null;
  const onlyReachable = only && companyUrl(only.slug, config, loc) ? only : null;
  // straight on when the company's page has this sign-in already: no second sign-in, nothing to press
  const onlySignedIn = onlyReachable ? signedInThere(companyUrl(onlyReachable.slug, config, loc)) : false;
  const goAtOnce = onlySignedIn && !choosing;
  useEffect(() => {
    if (onlyReachable && goAtOnce) go(onlyReachable);
    // go once per answer; `go` itself changes every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyReachable?.key, goAtOnce]);

  if (error)
    return (
      <div className="signin">
        <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />
        <Button onClick={signOut}>Sign out</Button>
      </div>
    );
  if (!access) {
    return (
      <div className="signin">
        <Loading what="your companies" />
      </div>
    );
  }
  if (choices.length === 0) {
    return (
      <Card title="Your account is not linked to a company yet">
        <p className="body">
          You are signed in as <strong>{email}</strong>, but no company using Teslyn has invited this address.
          Check the address in your invitation email, or ask the person who invited you.
        </p>
        <Button onClick={signOut}>Sign out</Button>
      </Card>
    );
  }
  if (onlyReachable && goAtOnce) return <Opening what={onlyReachable.what} />;
  if (onlyReachable) {
    const c = onlyReachable;
    return (
      <Card title={`Continue to ${c.what}`}>
        <p className="body">
          {onlySignedIn ? `You are signed in as ${email}. ${STAY_SIGNED_IN}` : SIGN_IN_AGAIN}
        </p>
        <Button variant="primary" onClick={() => go(c)}>
          Continue
        </Button>
        <Button variant="quiet" onClick={signOut}>
          Sign out
        </Button>
      </Card>
    );
  }
  const reachable = choices.map((c) => companyUrl(c.slug, config, loc)).filter((t) => t !== null);
  const allSignedIn = reachable.length > 0 && reachable.every(signedInThere);
  return (
    <Card title="Choose your company">
      <p className="body muted">
        Your account can sign in to more than one place in Teslyn Cloud.{' '}
        {allSignedIn ? STAY_SIGNED_IN : SIGN_IN_AGAIN}
      </p>
      <ul className="chooser">
        {choices.map((c) => {
          const reachable = companyUrl(c.slug, config, loc) !== null;
          return (
            <li key={c.key}>
              <button
                type="button"
                className="chooser__item"
                disabled={!reachable}
                aria-describedby={reachable ? undefined : `${c.key}-note`}
                onClick={() => go(c)}
              >
                <span className="heading">{c.title}</span>
                <span className="caption muted">{c.detail}</span>
                {!reachable && (
                  <span id={`${c.key}-note`} className="caption muted">
                    Address not set up yet. Ask Braynewave.
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      <Button variant="quiet" onClick={signOut}>
        Sign out
      </Button>
    </Card>
  );
}

function Card({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="frontdoor-title">
        <img className="signin__logo" src={lockup} alt="Teslyn" />
        <h1 id="frontdoor-title" className="title">
          {title}
        </h1>
        {children}
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks search="" />
      </footer>
    </div>
  );
}
